import type { DroitsImage } from '@artosera/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Bilingue, TypeMedia, VarianteMedia } from '@artosera/shared';
import { api, requeteMultipart, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Médias — `/v1/g/:slug/medias` (`docs/dev/api.md` § 13, 12.8, § 15.11). La
 * dépose est `multipart/form-data` ; la réponse ne porte que des clés de
 * stockage — la régie demande une URL signée d'aperçu par `?apercu=1` (deux
 * minutes). Rattachement `exhibition` ouvert depuis S3-d (§ 15.11) : les vues
 * d'accrochage d'une exposition sont des médias comme les autres.
 */
export type RattachementMedia = 'artwork' | 'artist' | 'exhibition';

export interface MediaPresente {
  readonly id: string;
  readonly cleOriginale: string;
  readonly contentType: string;
  readonly nomFichier: string;
  readonly octets: number;
  readonly largeur: number;
  readonly hauteur: number;
  readonly variantes: readonly VarianteMedia[];
  readonly type: TypeMedia;
  readonly rattachement: { readonly type: RattachementMedia; readonly id: string };
  readonly droits?: DroitsImage | null;
  readonly credit: string | null;
  readonly legende: Bilingue | null;
  readonly ordre: number;
  readonly supprime: boolean;
  readonly creeLe: string;
  readonly modifieLe: string;
}

export interface ApercuMedia {
  readonly media: MediaPresente;
  readonly apercu: { readonly url: string; readonly expireDansSecondes: number };
}

export interface CorpsDepotMedia {
  fichier: File;
  rattachementType: RattachementMedia;
  rattachementId: string;
  type?: TypeMedia;
  credit?: string;
  legendeFr?: string;
  legendeEn?: string;
  ordre?: number;
}

export interface CorpsMiseAJourMedia {
  type?: TypeMedia;
  credit?: string | null;
  legende?: { fr: string; en?: string } | null;
  ordre?: number;
}

const cleApercu = (galerie: string, id: string) => cleMetier(galerie, 'medias', 'apercu', id);
const cleListeRattachement = (galerie: string, type: string, id: string) =>
  cleMetier(galerie, 'medias', 'rattachement', type, id);

export const mediasApi = {
  lister: (
    galerie: string,
    rattachementType: RattachementMedia,
    rattachementId: string,
    appel?: AppelApi,
  ) =>
    api.get<{ medias: readonly MediaPresente[]; pagination: Pagination }>(
      `/g/${encodeURIComponent(galerie)}/medias?rattachementType=${rattachementType}&rattachementId=${rattachementId}&parPage=200`,
      { galerie, ...appel },
    ),
  /**
   * Tous les médias de la galerie, sans filtre de rattachement (`GET …/medias`
   * accepte les deux paramètres en option, `medias.routes.ts`). Sert le
   * sélecteur de logotype du site (S6-f) : la charte pointe vers un média
   * EXISTANT de la galerie (`docs/dev/api.md` § 15.6) — il n'existe pas de
   * cible de dépose dédiée à un logo (§ 15.11 n'ouvre qu'`artwork` / `artist`
   * / `exhibition` / `post`).
   */
  listerGalerie: (galerie: string, appel?: AppelApi) =>
    api.get<{ medias: readonly MediaPresente[]; pagination: Pagination }>(
      `/g/${encodeURIComponent(galerie)}/medias?parPage=200`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, apercu = false, appel?: AppelApi) =>
    api.get<ApercuMedia | { media: MediaPresente }>(
      `/g/${encodeURIComponent(galerie)}/medias/${id}${apercu ? '?apercu=1' : ''}`,
      { galerie, ...appel },
    ),
  deposer: (galerie: string, corps: CorpsDepotMedia, appel?: AppelApi) => {
    const donnees = new FormData();
    donnees.set('fichier', corps.fichier);
    donnees.set('rattachementType', corps.rattachementType);
    donnees.set('rattachementId', corps.rattachementId);
    if (corps.type) donnees.set('type', corps.type);
    if (corps.credit) donnees.set('credit', corps.credit);
    if (corps.legendeFr) donnees.set('legendeFr', corps.legendeFr);
    if (corps.legendeEn) donnees.set('legendeEn', corps.legendeEn);
    if (corps.ordre !== undefined) donnees.set('ordre', String(corps.ordre));
    return requeteMultipart<{ media: MediaPresente; variantes: { travailId: string } }>(
      `/g/${encodeURIComponent(galerie)}/medias`,
      donnees,
      { galerie, ...appel },
    );
  },
  modifier: (galerie: string, id: string, corps: CorpsMiseAJourMedia, appel?: AppelApi) =>
    api.patch<{ media: MediaPresente }>(`/g/${encodeURIComponent(galerie)}/medias/${id}`, corps, {
      galerie,
      ...appel,
    }),
  supprimer: (galerie: string, id: string, appel?: AppelApi) =>
    api.delete<{ media: MediaPresente }>(`/g/${encodeURIComponent(galerie)}/medias/${id}`, {
      galerie,
      ...appel,
    }),
};

/** URL signée d'aperçu (deux minutes) : la vue principale d'une œuvre ou d'un artiste. */
export function useApercuMedia(galerie: string, mediaId: string | null) {
  return useQuery({
    queryKey: cleApercu(galerie, mediaId ?? ''),
    queryFn: async (contexte) => {
      const reponse = await mediasApi.obtenir(
        galerie,
        mediaId ?? '',
        true,
        appelDeRequete(contexte),
      );
      return (reponse as ApercuMedia).apercu.url;
    },
    enabled: mediaId !== null,
    // L'URL signée expire en 2 minutes : jamais gardée plus vieille que ça.
    staleTime: 90_000,
  });
}

/** Tous les médias de la galerie : sélecteur de logotype (S6-f). */
export function useMediasGalerie(galerie: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'medias', 'galerie'),
    queryFn: (contexte) => mediasApi.listerGalerie(galerie, appelDeRequete(contexte)),
    select: (reponse) => reponse.medias,
  });
}

export function useMediasDeRattachement(
  galerie: string,
  rattachementType: RattachementMedia,
  rattachementId: string | null,
) {
  return useQuery({
    queryKey: cleListeRattachement(galerie, rattachementType, rattachementId ?? ''),
    queryFn: (contexte) =>
      mediasApi.lister(galerie, rattachementType, rattachementId ?? '', appelDeRequete(contexte)),
    select: (reponse) => reponse.medias,
    enabled: rattachementId !== null,
  });
}

export function useDeposerMedia(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsDepotMedia, appel) => mediasApi.deposer(galerie, corps, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'medias') });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'oeuvres', 'fiche') });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'expositions', 'fiche') });
      },
    }),
  );
}

export function useModifierMedia(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsMiseAJourMedia }, appel) =>
        mediasApi.modifier(galerie, id, corps, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'medias') });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'oeuvres', 'fiche') });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'expositions', 'fiche') });
      },
    }),
  );
}

export function useSupprimerMedia(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => mediasApi.supprimer(galerie, id, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'medias') });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'oeuvres', 'fiche') });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'expositions', 'fiche') });
      },
    }),
  );
}

export function useModifierDroitsImage(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, droits }: { id: string; droits: DroitsImage | null }, appel) =>
        api.patch(
          `/g/${encodeURIComponent(galerie)}/medias/${encodeURIComponent(id)}/droits`,
          { droits },
          { galerie, ...appel },
        ),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'medias') });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'oeuvres') });
      },
    }),
  );
}
